import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowUpRight, Check, ChevronLeft, Copy } from "lucide-react";
import { toast } from "sonner";
import { OverviewSectionCard } from "@/components/agent/overview/OverviewSectionCard";
import PageHeader from "@/components/common/PageHeader/PageHeader";
import { Button } from "@/components/ui/button";
import { GenieLoader } from "@/components/ui/GenieLoader";
import { Switch } from "@/components/ui/switch";
import { useAgent } from "@/hooks/agent";
import { useDocuments } from "@/hooks/documents/useDocumentFetch";
import { useRecommendPageContent } from "@/hooks/genie-documents/useRecommendPageContent";
import { useHubConfig } from "@/hooks/help-hub/useHubConfig";
import { usePageDraft } from "@/hooks/help-hub/usePageDraft";
import { PageDraftReview } from "./components/PageDraftReview";
import { useKnowledgeGaps } from "@/hooks/knowledge/useKnowledgeGaps";
import type {
  HubContactPhone,
  HubCustomSection,
  HubFeaturedQuestion,
} from "@/types/agent/info-links";
import type {
  GeniePageHeroIdea,
  RecommendGeniePageContentResult,
} from "@/types/genie-documents/genie-document";
import type { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";
import { DEFAULT_BRANDING } from "@/types/ui/branding";
import { ContactPhonesPicker, type ContactPhoneOption } from "./components/ContactPhonesPicker";
import { CreateGeniePageCard } from "./components/CreateGeniePageCard";
import { CustomSectionsEditor } from "./components/CustomSectionsEditor";
import { FeaturedQuestionsPicker } from "./components/FeaturedQuestionsPicker";
import { HubPreviewFrame } from "./components/HubPreviewFrame";
import { PageQRCodeCard } from "./components/PageQRCodeCard";
import { RecommendFromDocumentsButton } from "./components/RecommendFromDocumentsButton";
import { PageReadinessStrip, type ReadinessStep } from "./components/PageReadinessStrip";
import { PublishGuidesPanel } from "./components/PublishGuidesPanel";
import { geniePagePath, geniePageUrl, isPageLive, pageBlockedReason } from "./geniePageStatus";

/** Guides a page needs before it may ask search engines to index it. Mirrors the server. */
const MIN_GUIDES_FOR_INDEXING = 3;
/** Featured questions that count as "the page leads with something". */
const ENOUGH_QUESTIONS = 3;

const MAX_HEADLINE = 120;
const MAX_SUBHEADLINE = 240;

const inputClass =
  "w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-genie-primary";

/**
 * The owner's control panel for one Genie Page, at
 * `/genie-hub/genie-pages/:genieId`.
 *
 * Every card writes one slice of `agents.hub_config`; the real page renders beside
 * it in an iframe. Content itself is never edited here — guides are published
 * in the knowledge base and manuals are attached to the Genie's Resources —
 * this page decides how what already exists is arranged and led with.
 */
const GeniePageEditor = () => {
  const { genieId } = useParams<{ genieId: string }>();
  const navigate = useNavigate();
  const { data: genie, isLoading } = useAgent(genieId!);
  const { data: documents = [] } = useDocuments("my-documents", { agentId: genieId });
  const genieList = useMemo(() => (genie ? [genie] : []), [genie]);
  const { gaps } = useKnowledgeGaps(genieList);
  const { config, save, isSaving } = useHubConfig(genie);
  const pageDraft = usePageDraft(genieId, documents);

  const recommendContent = useRecommendPageContent();
  const [contentIdeas, setContentIdeas] = useState<RecommendGeniePageContentResult | null>(null);

  const [editingCopy, setEditingCopy] = useState(false);
  const [headline, setHeadline] = useState("");
  const [subheadline, setSubheadline] = useState("");
  const [askPlaceholder, setAskPlaceholder] = useState("");

  const [editingQuestions, setEditingQuestions] = useState(false);
  const [questions, setQuestions] = useState<HubFeaturedQuestion[]>([]);

  const [editingSections, setEditingSections] = useState(false);
  const [customSections, setCustomSections] = useState<HubCustomSection[]>([]);

  const [editingContact, setEditingContact] = useState(false);
  const [hours, setHours] = useState("");
  const [address, setAddress] = useState("");
  const [mapUrl, setMapUrl] = useState("");
  const [contactPhones, setContactPhones] = useState<HubContactPhone[]>([]);

  const [copied, setCopied] = useState(false);

  // Only created documents count — raw sources are never served as guides.
  const publishedGuides = useMemo(
    () =>
      (documents as DocumentWithMetadata[]).filter(
        (document) => document.is_public && document.source_type === "genie-document",
      ),
    [documents],
  );

  // Only knowledge sources feed the recommender — never a generated guide.
  const sourceCount = useMemo(
    () =>
      (documents as DocumentWithMetadata[]).filter((doc) => doc.source_type !== "genie-document")
        .length,
    [documents],
  );

  // One read of the sources fills every card's suggestions — hero, sections,
  // contact — so the first "Recommend" click arms all three.
  const handleRecommendContent = async () => {
    if (!genieId) return;
    try {
      const ideas = await recommendContent.mutateAsync(genieId);
      setContentIdeas(ideas);
      const count =
        ideas.heroes.length + ideas.sections.length + (ideas.contact ? 1 : 0);
      toast.success(
        `Drafted ${count} idea${count === 1 ? "" : "s"} from your sources — tap one to use it.`,
      );
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Couldn't draft content from your documents",
      );
    }
  };

  const applyHeroIdea = (idea: GeniePageHeroIdea) => {
    setHeadline(idea.headline);
    setSubheadline(idea.subheadline);
    setAskPlaceholder(idea.askPlaceholder);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F9FAFC]">
        <div className="container mx-auto max-w-[1920px] px-4 py-6 md:py-8">
          <GenieLoader size="block" loadingText="Loading your Genie Page…" className="py-16" />
        </div>
      </div>
    );
  }

  if (!genie) {
    return (
      <div className="min-h-screen bg-[#F9FAFC]">
        <div className="container mx-auto max-w-[1920px] px-4 py-6 md:py-8">
          <div className="mx-auto max-w-md rounded-2xl border border-gray-200 bg-white px-6 py-10 text-center">
            <h2 className="text-lg font-semibold text-gray-900">We couldn't find that Genie</h2>
            <Button variant="outline" className="mt-5" onClick={() => navigate("/genie-hub/genie-pages")}>
              Back to Genie Pages
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const brand = genie.branding?.primaryColor || DEFAULT_BRANDING.primaryColor;
  const url = geniePageUrl(genie);
  const path = geniePagePath(genie);
  const needsApproval = pageDraft.query.data?.needsApproval !== false;
  const live = isPageLive(genie) && !needsApproval;
  const blockedReason = needsApproval ? "Review and approve your first page content below." : pageBlockedReason(genie);
  const featured = config.featuredQuestions ?? [];
  const canIndex = publishedGuides.length >= MIN_GUIDES_FOR_INDEXING;

  // Lines that ring a person. Never on the page unless the owner adds them.
  const phoneOptions: ContactPhoneOption[] = [
    ...(genie.transfer_phones ?? []).map((phone) => ({ phone, kind: "Transfer number" as const })),
    ...(genie.escalation_phones ?? []).map((phone) => ({ phone, kind: "Escalation number" as const })),
  ];
  // A saved pick whose number has since left the Genie is no longer shown.
  const shownPhones = (config.contact?.phones ?? []).flatMap((pick) => {
    const option = phoneOptions.find(({ phone }) => String(phone.id) === pick.id);
    return option ? [{ ...pick, number: option.phone.number }] : [];
  });

  const readiness: ReadinessStep[] = [
    {
      key: "public",
      label: "Genie is public with a page address",
      done: live,
      href: `/genie-hub/my-genies/${genie.id}`,
    },
    {
      key: "guides",
      label: "At least one published document",
      done: publishedGuides.length > 0,
      href: `/genie-hub/knowledge-base/${genie.id}/documents`,
    },
    {
      key: "questions",
      label: `${ENOUGH_QUESTIONS}+ questions on the page`,
      done: featured.length >= ENOUGH_QUESTIONS,
    },
    {
      key: "reach",
      label: "A phone number or email address",
      done: Boolean(genie.phone?.number || genie.mailbox?.email_address),
      href: `/genie-hub/my-genies/${genie.id}`,
    },
    {
      key: "copy",
      label: "Your own hero headline",
      done: Boolean(config.headline?.trim()),
    },
    {
      key: "manuals",
      label: "Manuals or links attached",
      done: Boolean(genie.page?.info_links?.documents?.length || genie.page?.info_links?.links?.length),
      href: `/genie-hub/my-genies/${genie.id}`,
    },
  ];

  const handleCopy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Page link copied");
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy the link — you can select it from the page instead.");
    }
  };

  const startEditingCopy = () => {
    setHeadline(config.headline ?? "");
    setSubheadline(config.subheadline ?? "");
    setAskPlaceholder(config.askPlaceholder ?? "");
    setEditingCopy(true);
  };

  const saveCopy = async () => {
    await save(
      {
        headline: headline.trim().slice(0, MAX_HEADLINE) || null,
        subheadline: subheadline.trim().slice(0, MAX_SUBHEADLINE) || null,
        askPlaceholder: askPlaceholder.trim().slice(0, MAX_HEADLINE) || null,
      },
      "Hero updated",
    );
    setEditingCopy(false);
  };

  const saveQuestions = async () => {
    await save({ featuredQuestions: questions }, "Questions updated");
    setEditingQuestions(false);
  };

  const saveCustomSections = async () => {
    // Drop what can't render: untitled sections, and fields missing either half.
    const cleaned = customSections
      .map((section) => ({
        ...section,
        title: section.title.trim(),
        intro: section.intro?.trim() || null,
        fields: section.fields
          .map((field) => ({ ...field, label: field.label.trim(), value: field.value.trim() }))
          .filter((field) => field.label && field.value),
      }))
      .filter((section) => section.title && (section.intro || section.fields.length > 0));
    await save({ customSections: cleaned }, "Sections updated");
    setEditingSections(false);
  };

  const saveContact = async () => {
    const next = {
      hours: hours.trim() || null,
      address: address.trim() || null,
      mapUrl: mapUrl.trim() || null,
      phones: contactPhones.map((phone) => ({ id: phone.id, label: phone.label?.trim() || null })),
    };
    const hasAny = Boolean(next.hours || next.address || next.mapUrl || next.phones.length > 0);
    await save({ contact: hasAny ? next : null }, "Contact details updated");
    setEditingContact(false);
  };

  return (
    <div className="min-h-screen bg-[#F9FAFC]">
      <div className="container mx-auto max-w-[1920px] px-4 py-6 md:py-8">
        <Link
          to="/genie-hub/genie-pages"
          className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 transition-colors hover:text-gray-800"
        >
          <ChevronLeft className="h-4 w-4" />
          All Genie Pages
        </Link>

        <PageHeader
          title={genie.name || "Genie Page"}
          genieIcon="web-page"
          subtitle={
            url
              ? `${url.replace(/^https?:\/\//, "")} — one public page for everything this Genie can help with.`
              : "This Genie needs a page address before it can be published."
          }
        />

        <div className="grid grid-cols-[minmax(0,1fr)] gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(360px,46%)]">
          <div className="min-w-0 space-y-6">
            <PageReadinessStrip steps={readiness} />

            <OverviewSectionCard title="Your page draft" subtitle="Prepared from your knowledge, ready for your review"
              genieIcon="document" brand={brand}
              secondaryColor={genie.branding?.secondaryColor} gradientEnabled={genie.branding?.gradientEnabled}
              gradientAngle={genie.branding?.gradientAngle}>
              <PageDraftReview state={pageDraft.query.data} isLoading={pageDraft.query.isLoading}
                error={pageDraft.query.error} busy={pageDraft.generate.isPending || pageDraft.review.isPending}
                savedGuides={pageDraft.savedGuides} hiddenAfterApproval={pageDraft.hiddenAfterApproval}
                documentLibraryPath={`/genie-hub/knowledge-base/${genie.id}/documents`}
                onGenerate={() => pageDraft.generate.mutate()} onReview={(decision) => pageDraft.review.mutate(decision)} />
            </OverviewSectionCard>
            {/* One-command page creation (cursor/genie-page-content-ideas-2340) is merged but not rendered: it overlaps the page draft card above. Swap in <CreateGeniePageCard> to try it. */}

            {/* Status + link */}
            <OverviewSectionCard
              title="Page status"
              subtitle="Where this page lives and whether anyone can reach it"
              genieIcon="globe"
              brand={brand}
              secondaryColor={genie.branding?.secondaryColor}
              gradientEnabled={genie.branding?.gradientEnabled}
              gradientAngle={genie.branding?.gradientAngle}
            >
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {live ? "Live" : "Not published"}
                    </p>
                    <p className="mt-0.5 text-[13px] text-gray-600">
                      {blockedReason ?? "Anyone with the link can reach this page."}
                    </p>
                  </div>
                  <Switch
                    checked={config.enabled !== false}
                     disabled={isSaving || needsApproval}
                    onCheckedChange={(checked) =>
                      void save({ enabled: checked }, checked ? "Page switched on" : "Page switched off")
                    }
                    aria-label="Publish this page"
                  />
                </div>

                {url && (
                  <div className="flex flex-wrap gap-2">
                    <a
                      href={url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="inline-flex items-center gap-1.5 rounded-xl px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                      style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
                    >
                      Open the page
                      <ArrowUpRight className="h-4 w-4" />
                    </a>
                    <Button variant="outline" className="gap-1.5" onClick={() => void handleCopy()}>
                      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      Copy link
                    </Button>
                  </div>
                )}
              </div>
            </OverviewSectionCard>

            {/* QR code that opens the page */}
            {url && (
              <OverviewSectionCard
                title="QR code"
                subtitle="A scan that opens this whole page, ready to print"
                genieIcon="qr-code"
                brand={brand}
                secondaryColor={genie.branding?.secondaryColor}
                gradientEnabled={genie.branding?.gradientEnabled}
                gradientAngle={genie.branding?.gradientAngle}
              >
                <PageQRCodeCard genie={genie} url={url} />
              </OverviewSectionCard>
            )}

            {/* Hero copy */}
            <OverviewSectionCard
              title="Hero"
              subtitle="The first thing a visitor reads, and what the ask box invites"
              genieIcon="palette"
              brand={brand}
              secondaryColor={genie.branding?.secondaryColor}
              gradientEnabled={genie.branding?.gradientEnabled}
              gradientAngle={genie.branding?.gradientAngle}
              onEdit={editingCopy ? undefined : startEditingCopy}
              onSave={editingCopy ? () => void saveCopy() : undefined}
              onCancel={editingCopy ? () => setEditingCopy(false) : undefined}
              isSaving={isSaving}
            >
              {editingCopy ? (
                <div className="space-y-4">
                  <div>
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <span className="text-sm font-medium text-gray-700">Suggestions</span>
                      <RecommendFromDocumentsButton
                        onClick={() => void handleRecommendContent()}
                        isPending={recommendContent.isPending}
                        hasSources={sourceCount > 0}
                      />
                    </div>
                    {contentIdeas === null ? (
                      <p className="rounded-xl bg-gray-50 px-4 py-3 text-[13px] leading-snug text-gray-600">
                        {sourceCount === 0
                          ? "Add a source to this Genie's knowledge base first — then it can draft hero copy from what it knows."
                          : "Tap “Recommend from my documents” and your Genie will read its sources and draft hero copy — tap a draft to load it into the fields below."}
                      </p>
                    ) : contentIdeas.heroes.length > 0 ? (
                      <ul className="space-y-2">
                        {contentIdeas.heroes.map((hero) => (
                          <li key={hero.headline}>
                            <button
                              type="button"
                              onClick={() => applyHeroIdea(hero)}
                              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-left transition-colors hover:border-genie-primary/50 hover:bg-genie-primary/5"
                            >
                              <p className="text-sm font-medium text-gray-900">{hero.headline}</p>
                              <p className="mt-0.5 text-[12.5px] leading-snug text-gray-500">
                                {hero.subheadline}
                              </p>
                              <p className="mt-1 text-[12px] text-gray-400">
                                Ask box: {hero.askPlaceholder}
                              </p>
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="rounded-xl bg-gray-50 px-4 py-3 text-[13px] leading-snug text-gray-600">
                        No hero copy in this draft — try again, or write it below.
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700">Headline</label>
                    <input
                      value={headline}
                      onChange={(event) => setHeadline(event.target.value.slice(0, MAX_HEADLINE))}
                      placeholder="A question? Let’s work through it."
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700">
                      Supporting line
                    </label>
                    <textarea
                      value={subheadline}
                      onChange={(event) => setSubheadline(event.target.value.slice(0, MAX_SUBHEADLINE))}
                      rows={2}
                      placeholder="Ask in your own words — someone's here either way."
                      className={`${inputClass} resize-y`}
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700">
                      Ask box placeholder
                    </label>
                    <input
                      value={askPlaceholder}
                      onChange={(event) => setAskPlaceholder(event.target.value.slice(0, MAX_HEADLINE))}
                      placeholder="Ask anything — in your own words"
                      className={inputClass}
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    Leave any of these blank to use the default. Your logo and colours come from
                    Branding.
                  </p>
                </div>
              ) : (
                <dl className="space-y-3 text-sm">
                  <div>
                    <dt className="text-gray-500">Headline</dt>
                    <dd className="text-gray-900">{config.headline || "A question? Let’s work through it."}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Supporting line</dt>
                    <dd className="text-gray-900">
                      {config.subheadline ||
                        genie.page?.welcome_message ||
                        genie.welcome_message ||
                        "—"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Ask box</dt>
                    <dd className="text-gray-900">
                      {config.askPlaceholder || "Ask anything — in your own words"}
                    </dd>
                  </div>
                </dl>
              )}
            </OverviewSectionCard>

            {/* Guides published to the page */}
            <div id="saved-page-guides" className="scroll-mt-6">
              <OverviewSectionCard
                title="Saved guides"
                subtitle="Your saved work — switch on a guide to make it visible on the page"
                genieIcon="book-open"
                brand={brand}
                secondaryColor={genie.branding?.secondaryColor}
                gradientEnabled={genie.branding?.gradientEnabled}
                gradientAngle={genie.branding?.gradientAngle}
              >
                <PublishGuidesPanel
                  genieId={genie.id}
                  documents={documents as DocumentWithMetadata[]}
                />
              </OverviewSectionCard>
            </div>

            {/* Featured questions */}
            <OverviewSectionCard
              title="Questions on the page"
              subtitle="Tapping one opens the Genie and asks it — answers are always live"
              genieIcon="chat-check"
              brand={brand}
              secondaryColor={genie.branding?.secondaryColor}
              gradientEnabled={genie.branding?.gradientEnabled}
              gradientAngle={genie.branding?.gradientAngle}
              onEdit={
                editingQuestions
                  ? undefined
                  : () => {
                    setQuestions(featured);
                    setEditingQuestions(true);
                  }
              }
              onSave={editingQuestions ? () => void saveQuestions() : undefined}
              onCancel={editingQuestions ? () => setEditingQuestions(false) : undefined}
              isSaving={isSaving}
            >
              {editingQuestions ? (
                <FeaturedQuestionsPicker
                  value={questions}
                  onChange={setQuestions}
                  documents={documents as DocumentWithMetadata[]}
                  askedQuestions={gaps.askedQuestions}
                  genieId={genie.id}
                />
              ) : featured.length > 0 ? (
                <ul className="space-y-2">
                  {featured.map((entry) => (
                    <li
                      key={entry.id}
                      className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800"
                    >
                      {entry.question}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="rounded-xl bg-gray-50 px-4 py-3 text-[13px] leading-snug text-gray-600">
                  Nothing picked, so the page shows every question your published guides say they
                  answer. That's a good default — edit to lead with the ones that matter most.
                </p>
              )}
            </OverviewSectionCard>

            {/* Sections */}
            <OverviewSectionCard
              title="Sections"
              subtitle="Which bands appear below the hero"
              genieIcon="dashboard"
              brand={brand}
              secondaryColor={genie.branding?.secondaryColor}
              gradientEnabled={genie.branding?.gradientEnabled}
              gradientAngle={genie.branding?.gradientAngle}
            >
              <div className="divide-y divide-gray-100">
                {([
                  {
                    key: "showFaq" as const,
                    label: "What people ask us",
                    detail: `${featured.length > 0 ? featured.length : "All"} questions`,
                  },
                  {
                    key: "showGuides" as const,
                    label: "Guides",
                    detail: `${publishedGuides.length} published`,
                  },
                  {
                    key: "showDownloads" as const,
                    label: "Manuals & documents",
                    detail: `${genie.page?.info_links?.documents?.length ?? 0} attached`,
                  },
                  {
                    key: "showApp" as const,
                    label: "Take us with you",
                    detail: "The app, with this Genie kept in it",
                  },
                ]).map((section) => (
                  <div key={section.key} className="flex items-center justify-between gap-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{section.label}</p>
                      <p className="text-[12.5px] text-gray-500">{section.detail}</p>
                    </div>
                    <Switch
                      checked={config[section.key] !== false}
                      disabled={isSaving}
                      onCheckedChange={(checked) => void save({ [section.key]: checked })}
                      aria-label={section.label}
                    />
                  </div>
                ))}
              </div>
            </OverviewSectionCard>

            {/* Owner-written sections */}
            <OverviewSectionCard
              title="Your own sections"
              subtitle="Extra details the fixed bands don't carry — an address, account numbers, holiday hours"
              genieIcon="document"
              brand={brand}
              secondaryColor={genie.branding?.secondaryColor}
              gradientEnabled={genie.branding?.gradientEnabled}
              gradientAngle={genie.branding?.gradientAngle}
              onEdit={
                editingSections
                  ? undefined
                  : () => {
                    setCustomSections(config.customSections ?? []);
                    setEditingSections(true);
                  }
              }
              onSave={editingSections ? () => void saveCustomSections() : undefined}
              onCancel={editingSections ? () => setEditingSections(false) : undefined}
              isSaving={isSaving}
            >
              {editingSections ? (
                <CustomSectionsEditor
                  value={customSections}
                  onChange={setCustomSections}
                  ideas={contentIdeas?.sections ?? null}
                  onRecommend={() => void handleRecommendContent()}
                  isRecommending={recommendContent.isPending}
                  hasSources={sourceCount > 0}
                />
              ) : (config.customSections?.length ?? 0) > 0 ? (
                <ul className="space-y-2">
                  {config.customSections!.map((section) => (
                    <li
                      key={section.id}
                      className="rounded-xl border border-gray-200 bg-white px-3 py-2.5"
                    >
                      <p className="text-sm font-medium text-gray-900">{section.title}</p>
                      <p className="text-[12.5px] text-gray-500">
                        {section.fields.length}{" "}
                        {section.fields.length === 1 ? "field" : "fields"}
                        {section.intro ? " · with a lead-in" : ""}
                      </p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="rounded-xl bg-gray-50 px-4 py-3 text-[13px] leading-snug text-gray-600">
                  Nothing yet. Add a section for anything your visitors keep needing —
                  it appears on the page styled like every other band.
                </p>
              )}
            </OverviewSectionCard>

            {/* Contact */}
            <OverviewSectionCard
              title="Reach us"
              subtitle="Hours, where to find you, and any extra numbers. Your Genie's own number and email appear automatically."
              genieIcon="phone"
              brand={brand}
              secondaryColor={genie.branding?.secondaryColor}
              gradientEnabled={genie.branding?.gradientEnabled}
              gradientAngle={genie.branding?.gradientAngle}
              onEdit={
                editingContact
                  ? undefined
                  : () => {
                    setHours(config.contact?.hours ?? "");
                    setAddress(config.contact?.address ?? "");
                    setMapUrl(config.contact?.mapUrl ?? "");
                    setContactPhones(shownPhones.map(({ id, label }) => ({ id, label: label ?? "" })));
                    setEditingContact(true);
                  }
              }
              onSave={editingContact ? () => void saveContact() : undefined}
              onCancel={editingContact ? () => setEditingContact(false) : undefined}
              isSaving={isSaving}
            >
              {editingContact ? (
                <div className="space-y-4">
                  <div>
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <span className="text-sm font-medium text-gray-700">Suggestions</span>
                      <RecommendFromDocumentsButton
                        onClick={() => void handleRecommendContent()}
                        isPending={recommendContent.isPending}
                        hasSources={sourceCount > 0}
                      />
                    </div>
                    {contentIdeas === null ? (
                      <p className="rounded-xl bg-gray-50 px-4 py-3 text-[13px] leading-snug text-gray-600">
                        {sourceCount === 0
                          ? "Add a source to this Genie's knowledge base first — then it can pull hours and address from what it knows."
                          : "If your sources state your hours or address, “Recommend from my documents” will find them."}
                      </p>
                    ) : contentIdeas.contact ? (
                      <div className="flex items-start justify-between gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2.5">
                        <div className="min-w-0 text-[13px] leading-snug text-gray-700">
                          {contentIdeas.contact.hours && <p>Hours: {contentIdeas.contact.hours}</p>}
                          {contentIdeas.contact.address && (
                            <p>Address: {contentIdeas.contact.address}</p>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setHours(contentIdeas.contact?.hours ?? "");
                            setAddress(contentIdeas.contact?.address ?? "");
                          }}
                        >
                          Use these
                        </Button>
                      </div>
                    ) : (
                      <p className="rounded-xl bg-gray-50 px-4 py-3 text-[13px] leading-snug text-gray-600">
                        Your sources don't state hours or an address — add them below if you'd like
                        them on the page.
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700">Hours</label>
                    <input
                      value={hours}
                      onChange={(event) => setHours(event.target.value.slice(0, 200))}
                      placeholder="Mon–Fri, 8am–6pm"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700">Address</label>
                    <input
                      value={address}
                      onChange={(event) => setAddress(event.target.value.slice(0, 240))}
                      placeholder="12 Queen Street, Auckland"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700">Map link</label>
                    <input
                      value={mapUrl}
                      onChange={(event) => setMapUrl(event.target.value)}
                      placeholder="https://maps.google.com/…"
                      className={inputClass}
                    />
                  </div>
                  <ContactPhonesPicker
                    genieId={genie.id}
                    primaryNumber={genie.phone?.number ?? null}
                    options={phoneOptions}
                    value={contactPhones}
                    onChange={setContactPhones}
                  />
                </div>
              ) : (
                <dl className="space-y-3 text-sm">
                  <div>
                    <dt className="text-gray-500">Hours</dt>
                    <dd className="text-gray-900">{config.contact?.hours || "—"}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Address</dt>
                    <dd className="text-gray-900">{config.contact?.address || "—"}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Phone numbers</dt>
                    {genie.phone?.number || shownPhones.length > 0 ? (
                      <>
                        {genie.phone?.number && (
                          <dd className="text-gray-900">
                            {genie.phone.number}{" "}
                            <span className="text-gray-500">· your Genie's own number</span>
                          </dd>
                        )}
                        {shownPhones.map((phone) => (
                          <dd key={phone.id} className="text-gray-900">
                            {phone.number}{" "}
                            <span className="text-gray-500">· {phone.label || "Call us"}</span>
                          </dd>
                        ))}
                      </>
                    ) : (
                      <dd className="text-gray-900">—</dd>
                    )}
                  </div>
                </dl>
              )}
            </OverviewSectionCard>

            {/* Search visibility */}
            <OverviewSectionCard
              title="Search engines"
              subtitle="Whether this page can be found on Google"
              genieIcon="eye"
              brand={brand}
              secondaryColor={genie.branding?.secondaryColor}
              gradientEnabled={genie.branding?.gradientEnabled}
              gradientAngle={genie.branding?.gradientAngle}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {config.searchEngineVisible && canIndex ? "Findable on Google" : "Link only"}
                  </p>
                  <p className="mt-1 text-[13px] leading-snug text-gray-600">
                    {canIndex
                      ? "Turn this on and search engines can index the page, with your logo and description on the result."
                      : `Available once you've published ${MIN_GUIDES_FOR_INDEXING} guides — right now you have ${publishedGuides.length}. A thin page in search results reads worse than no page at all.`}
                  </p>
                </div>
                <Switch
                  checked={config.searchEngineVisible === true}
                  disabled={!canIndex || isSaving}
                  onCheckedChange={(checked) =>
                    void save(
                      { searchEngineVisible: checked },
                      checked ? "Page can now be indexed" : "Page is link-only again",
                    )
                  }
                  aria-label="Allow search engines to index this page"
                />
              </div>
            </OverviewSectionCard>
          </div>

          {/* Live preview */}
          <div className="min-w-0 xl:sticky xl:top-6 xl:self-start">
            {path && !needsApproval ? (
              <HubPreviewFrame key={pageDraft.query.dataUpdatedAt} src={path} title={`${genie.name} help centre preview`} />
            ) : (
              <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-600">
                {needsApproval ? "Your proposed guides and questions are in Your page draft. Approve them to open the public page preview." : "Give this Genie a page address and the live preview appears here."}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeniePageEditor;
